import * as React from 'react'
import Chat, { type ChatProps } from './Chat'
import { type SurveyOptions, useRequest } from './RequestsCtx'
import Loading from './Loading'
import type { Models } from '@packages/survey-api'
import { FrameVariant } from './api'
import { OffsetContainer } from './OffsetContainer'
import { InputAPI } from './Input'

export type PreviewOpeningQuestionId = string

export type WidgetProps = {
  surveyId: string
  setOpened: (opened: boolean) => void
  initLoading?: boolean
  meta: SurveyOptions
  onConfig?: (opts: { variant: FrameVariant }) => void
  onFirstClick?: () => void
  openingQuestionId?: PreviewOpeningQuestionId
} & Pick<
  ChatProps,
  'onInputFocus' | 'onInputBlur' | 'onHeightChange' | 'autoFocus'
>

const Widget = ({
  surveyId,
  setOpened,
  onHeightChange,
  onFirstClick,
  initLoading,
  meta,
  onConfig,
  openingQuestionId,
  ...chatProps
}: WidgetProps) => {
  const queryFn = useRequest(s => s.surveyQueryFn)

  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState<
    undefined | Extract<Models['OpenSurveyResponse'], { type: 'new-session' }>
  >(undefined)
  const inputApi = React.useRef<InputAPI>(null)
  const [isTouched, setTouched] = React.useState(false)

  React.useEffect(() => {
    let canceled = false
    queryFn({ surveyId, meta, questionId: openingQuestionId })
      .then(_data => {
        if (!canceled) {
          setLoading(false)

          if (_data.type === 'new-session') {
            setData(_data)
            onConfig?.({
              variant: _data.session.variant,
            })
          } else {
            setOpened(false)
          }
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e)
        if (!canceled) {
          setLoading(false)
          setOpened(false)
        }
      })

    return () => {
      canceled = true
    }
  }, [])

  if (loading) {
    return initLoading ? (
      <OffsetContainer
        isTouched={isTouched}
        variant="classic"
        inputApi={inputApi}>
        <Loading />
      </OffsetContainer>
    ) : null
  }

  if (!data) {
    return null
  }

  return (
    <OffsetContainer
      isTouched={isTouched}
      onUntouchedClick={() => {
        if (!isTouched) {
          setTouched(true)
          onFirstClick?.()
        }
      }}
      inputApi={inputApi}
      variant={data.session.variant}
      onHeightChange={onHeightChange}>
      <Chat
        survey={data}
        setOpened={setOpened}
        ref={inputApi}
        onHeightChange={onHeightChange}
        {...chatProps}
      />
    </OffsetContainer>
  )
}

export default Widget
